// Actions du cycle de vie d'une version : soumettre à vérification, renvoyer en brouillon avec
// motif, publier à quatre yeux avec le résumé des changements par rapport à la version publiée.
// Chaque succès n'est annoncé qu'après la réponse du serveur.
import type { ContenuVersion, Demarche } from '@cc/contrats';
import { Bandeau, Bouton, DialogueConfirmation, ZoneTexte } from '@cc/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { requete } from '../../api/client';
import { ErreurApi } from '../../api/erreurs';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { comparerContenus, etapesSupprimees, LIBELLES_NATURE, LIBELLES_ZONES, type Changement } from './comparaison';
import { useServices, type VersionDemarche } from './contenu';
import styles from './demarches.module.css';

export function ListeChangements({ changements, vide = 'Aucune différence.' }: { changements: Changement[]; vide?: string }) {
  if (changements.length === 0) return <p className={styles.aideDiscrete}>{vide}</p>;
  return (
    <ul className={styles.changements} aria-label="Changements">
      {changements.map((c, i) => (
        <li key={i}>
          <span className={styles.nature} data-nature={c.nature}>
            {LIBELLES_NATURE[c.nature]}
          </span>
          <span>
            {c.texte}
            <span className={styles.zone}>{LIBELLES_ZONES[c.zone]}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export interface ProprietesActionsVersion {
  demarche: Demarche;
  version: Pick<VersionDemarche, 'numero' | 'etat' | 'soumise_le'>;
  /** Contenu courant de l'éditeur (pour le résumé des changements). */
  contenu: ContenuVersion;
  /** Contenu de la version publiée, s'il y en a une. */
  contenuPublie: ContenuVersion | null;
  modifie: boolean;
}

export function ActionsVersion({ demarche, version, contenu, contenuPublie, modifie }: ProprietesActionsVersion) {
  const { contexte } = useSession();
  const navigate = useNavigate();
  const services = useServices();
  const [dialogue, setDialogue] = useState<'soumettre' | 'renvoyer' | 'publier' | null>(null);
  const [motif, setMotif] = useState('');
  const base = `/demarches/${demarche.id}/versions/${version.numero}`;
  const invalider = [['demarches'], ['indicateurs']];
  const fermer = () => {
    setDialogue(null);
    setMotif('');
  };
  const soumission = useAction({ executer: () => requete(`${base}/soumettre-verification`, { methode: 'POST' }), succes: `Version ${version.numero} soumise à vérification : une autre personne habilitée doit la publier.`, invalider, apres: fermer });
  const renvoi = useAction({ executer: () => requete(`${base}/renvoyer-brouillon`, { methode: 'POST', corps: { motif } }), succes: `Version ${version.numero} renvoyée en brouillon.`, invalider, apres: fermer });
  const publication = useAction({
    executer: () => requete(`${base}/publier`, { methode: 'POST' }),
    succes: `Version ${version.numero} publiée : elle s'applique aux nouveaux dossiers.`,
    invalider,
    apres: () => {
      fermer();
      navigate(`/demarches/${demarche.id}`);
    },
  });

  const peutModifier = peut(contexte, 'demarche_config', 'modifier');
  const peutValider = peut(contexte, 'demarche_config', 'valider');
  const changements = contenuPublie ? comparerContenus(contenuPublie, contenu, new Map((services.data ?? []).map((s) => [s.id, s.nom]))) : [];
  const supprimees = contenuPublie ? etapesSupprimees(contenuPublie, contenu) : [];
  const erreurPublication = publication.erreur instanceof ErreurApi ? publication.erreur : null;

  return (
    <>
      {version.etat === 'brouillon' && peutModifier ? (
        <Bouton variante={modifie ? 'secondaire' : 'principal'} disabled={modifie} title={modifie ? 'Enregistrez d\'abord vos modifications' : undefined} onClick={() => setDialogue('soumettre')}>
          Soumettre à vérification
        </Bouton>
      ) : null}
      {version.etat === 'en_verification' && peutValider ? (
        <>
          <Bouton variante="secondaire" onClick={() => setDialogue('renvoyer')}>
            Renvoyer en brouillon
          </Bouton>
          <Bouton variante="principal" onClick={() => setDialogue('publier')}>
            Publier
          </Bouton>
        </>
      ) : null}
      {version.etat === 'en_verification' && !peutValider ? <span className={styles.aideDiscrete}>En attente de publication par une personne disposant du droit de valider la configuration (secrétaire général).</span> : null}

      <DialogueConfirmation ouvert={dialogue === 'soumettre'} titre="Soumettre la version à vérification" libelleConfirmer="Soumettre" enCours={soumission.enCours} onConfirmer={() => soumission.lancer(undefined)} onAnnuler={fermer} message={<p>La version {version.numero} ne sera plus modifiable tant qu'elle est en vérification. Règle des quatre yeux : la personne qui soumet ne peut pas publier.</p>}>
        {soumission.messageErreur ? <Bandeau type="erreur">{soumission.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>

      <DialogueConfirmation ouvert={dialogue === 'renvoyer'} titre="Renvoyer la version en brouillon" libelleConfirmer="Renvoyer en brouillon" variante="secondaire" enCours={renvoi.enCours} confirmationDesactivee={motif.trim().length < 3} onConfirmer={() => renvoi.lancer(undefined)} onAnnuler={fermer} message={<p>La version redevient modifiable par l'administrateur fonctionnel ; le motif lui est transmis.</p>}>
        <ZoneTexte libelle="Motif du renvoi" value={motif} required onChange={(e) => setMotif(e.target.value)} />
        {renvoi.messageErreur ? <Bandeau type="erreur">{renvoi.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>

      <DialogueConfirmation ouvert={dialogue === 'publier'} titre={`Publier la version ${version.numero}`} libelleConfirmer="Publier la version" enCours={publication.enCours} onConfirmer={() => publication.lancer(undefined)} onAnnuler={fermer} message={<p>{contenuPublie ? `Ce que change la version ${version.numero} par rapport à la version publiée :` : 'Première publication de cette démarche : elle devient visible dans le catalogue et accepte des dépôts.'}</p>}>
        {contenuPublie ? <ListeChangements changements={changements} vide="Aucune différence avec la version publiée." /> : null}
        {supprimees.length > 0 ? <Bandeau type="attention">Étapes supprimées : {supprimees.join(', ')}. Les dossiers déjà arrivés à ces étapes ne pourront pas être migrés.</Bandeau> : null}
        {contenuPublie ? <p className={styles.aideDiscrete}>Les dossiers déposés avec la version précédente gardent leurs règles. Une migration reste possible depuis la fiche de la démarche, avec motif et rapport.</p> : null}
        {erreurPublication?.code === 'quatre_yeux' ? <Bandeau type="erreur">Règle des quatre yeux : vous avez soumis cette version, une autre personne doit la publier.</Bandeau> : publication.messageErreur ? <Bandeau type="erreur">{publication.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
    </>
  );
}
